// Gravity Well — creative timed event (Agent B), built on the space-storm
// timed-event mechanic. A strong temporary well opens at the arena center
// (0, 0) and pulls the player, all enemies, and all projectiles toward it.
// The pull ramps in over the first 0.75 s and eases out over the last
// 0.75 s so the event never jerks motion.
//
// Determinism (GDD Appendix A §A.1): pure function of world state and tick —
// no RNG, no Math.random, no wall-clock. Pool iteration is in pool_index
// order; all distances use the toroidal wrapped delta.
//
// Perf: one pass over enemies + projectiles per tick with a handful of
// flops each. Trivially inside the 60 fps budget.
//
// Visuals (wired by the parent, not here): listen for —
//   'phase_change' { system: 'events', event_id: 'gravity_well', phase: 'start'|'end', pos, radius, duration }
// Suggested look: dark vortex at the well center, swirling particle
// spirals, slight screen-space lensing toward the center while active.
//
// Integration note: update() must run AFTER updatePlayer/updateEnemies/
// updateProjectiles in the sim step (see game.ts) — the pull perturbs
// velocities that those systems integrate on the following tick.

import type { Vec2 } from '../../types.js';
import type { WorldState } from '../state.js';
import { wrappedDelta } from '../collision.js';
import { applyPlayerKnockback } from '../movement.js';
import type { GameEventDef } from '../events-manager.js';

/** Event lifetime: 8 s at 60 Hz. */
const EVENT_DURATION_TICKS = 8 * 60;
/** Well center: the arena center. */
const WELL_POS: Vec2 = { x: 0, y: 0 };
/** Pull radius (arena units); covers most of the 200×200 torus. */
const WELL_RADIUS = 70;
/**
 * Acceleration profile: accel = min(MAX_ACCEL, STRENGTH / max(dist², MIN_DIST²)).
 * At 10 u ≈ 48 (capped), 20 u ≈ 15, 30 u ≈ 6.7, 50 u ≈ 2.4 u/s² — before the
 * per-faction gain below.
 */
const STRENGTH = 6000;
const MAX_ACCEL = 48;
const MIN_DIST_SQ = 25; // softens the singularity at the core
/**
 * Faction gains: enemies get 2× so the pull visibly wins against their
 * steering (max_force 20 u/s²); projectiles get 1× (their velocity persists,
 * so the pull curves their paths into spirals); the player is pulled through
 * the movement system's knockback channel (see below) at 6×.
 */
const ENEMY_FORCE_GAIN = 2;
const PROJECTILE_FORCE_GAIN = 1;
const PLAYER_KNOCKBACK_GAIN = 6;
/** Ramp in/out window (0.75 s) applied to the overall strength. */
const RAMP_TICKS = 45;
/** Fixed sim step; events only ever run on the 60 Hz fixed step (game.ts STEP). */
const DT = 1 / 60;

/**
 * Create the Gravity Well event def. Stateless between activations — the
 * pull leaves no permanent velocity behind (enemy steering re-claims enemy
 * velocity, projectile curves simply remain, and the player's knockback
 * decays via the movement system's normal knockback decay).
 */
export function createGravityWellEvent(): GameEventDef {
  /**
   * The player is pulled via applyPlayerKnockback — the sanctioned channel
   * for external forces (movement.ts integrates knockback into position
   * independently of the input-driven accel model, which would otherwise
   * fight and erase direct `vel` pushes). With knockback_decay 40 u/s², a
   * sustained per-tick impulse of accel·g·dt settles into a drift speed of
   * ≈ accel·g/40 toward the well: strong near the core (~7 u/s capped),
   * escapable at range — the player can always outrun it at move_speed 8.
   */
  function pull(world: WorldState, mult: number): void {
    const apply = (pos: Vec2, vel: Vec2, gain: number): void => {
      const d = wrappedDelta(WELL_POS, pos); // direction from pos → well
      const distSq = d.x * d.x + d.y * d.y;
      if (distSq > WELL_RADIUS * WELL_RADIUS || distSq < 1e-4) return;
      const dist = Math.sqrt(distSq);
      const accel = Math.min(MAX_ACCEL, STRENGTH / Math.max(distSq, MIN_DIST_SQ)) * mult * gain;
      vel.x += (d.x / dist) * accel * DT;
      vel.y += (d.y / dist) * accel * DT;
    };

    if (world.player.alive) {
      const d = wrappedDelta(WELL_POS, world.player.pos);
      const distSq = d.x * d.x + d.y * d.y;
      if (distSq <= WELL_RADIUS * WELL_RADIUS && distSq >= 1e-4) {
        const dist = Math.sqrt(distSq);
        const accel =
          Math.min(MAX_ACCEL, STRENGTH / Math.max(distSq, MIN_DIST_SQ)) * mult * PLAYER_KNOCKBACK_GAIN;
        applyPlayerKnockback(
          world.player,
          { x: d.x / dist, y: d.y / dist },
          accel * DT
        );
      }
    }
    for (const e of world.enemies.alive()) {
      apply(e.pos, e.vel, ENEMY_FORCE_GAIN);
    }
    for (const p of world.projectiles.alive()) {
      apply(p.pos, p.vel, PROJECTILE_FORCE_GAIN);
    }
  }

  return {
    id: 'gravity_well',
    name: 'Gravity Well',
    duration: EVENT_DURATION_TICKS,

    start(world: WorldState, tick: number): void {
      world.events.emit(
        'phase_change',
        {
          system: 'events',
          event_id: 'gravity_well',
          phase: 'start',
          pos: { x: WELL_POS.x, y: WELL_POS.y },
          radius: WELL_RADIUS,
          duration: EVENT_DURATION_TICKS
        },
        tick
      );
    },

    update(world: WorldState, _tick: number, progress: number): void {
      // Smooth strength envelope: ramp in, full, ease out (deterministic —
      // derived from the manager-supplied progress, no wall-clock).
      const elapsed = progress * EVENT_DURATION_TICKS;
      const remaining = EVENT_DURATION_TICKS - elapsed;
      const mult = Math.min(1, elapsed / RAMP_TICKS, remaining / RAMP_TICKS);
      if (mult <= 0) return;
      pull(world, mult);
    },

    end(world: WorldState, tick: number): void {
      world.events.emit(
        'phase_change',
        {
          system: 'events',
          event_id: 'gravity_well',
          phase: 'end',
          pos: { x: WELL_POS.x, y: WELL_POS.y },
          radius: WELL_RADIUS
        },
        tick
      );
    }
  };
}
